import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { getStaticUrl } from "js/config";
import { t } from "js/i18n";

import { WorkspacePlanTier } from "common/interfaces";
import * as StripeUtils from "common/utils/stripeUtils";
import { formatPlanCardPrice } from "common/utils/stripeUtils";
import AuthController from "js/controllers/AuthController";

import { breakPoints, mediaMaxWidth, postGroteskFont, PrimaryButton } from "./Shared";
import * as TourCard from "./TourCard";

const stackBreakpoint = mediaMaxWidth(810);

const pricingAssetPath = "/images/onboardingVariantMay2026/pricing/";

// Sizes as defined in Figma designs
const trustLogos = [
    {
        alt: "eBay",
        src: getStaticUrl(`${pricingAssetPath}logo-ebay.svg`),
    },
    {
        alt: "Salesforce",
        src: getStaticUrl(`${pricingAssetPath}logo-salesforce.svg`),
    },
    {
        alt: "Google",
        src: getStaticUrl(`${pricingAssetPath}logo-google.svg`),
    },
    {
        alt: "HP",
        src: getStaticUrl(`${pricingAssetPath}logo-hp.svg`),
    },
    {
        alt: "Fandom",
        src: getStaticUrl(`${pricingAssetPath}logo-fandom.svg`),
    },
    {
        alt: "Phoenix Suns",
        src: getStaticUrl(`${pricingAssetPath}logo-suns.svg`),
    }
] as const;

const PricingContent = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 22px;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
    position: relative;
    width: 100%;

    ${breakPoints.lg`
        padding-left: 16px;
        padding-right: 16px;
    `}

    ${breakPoints.xs`
        gap: 20px;
        overflow: visible;
        padding: 4px 24px 24px;
    `}
`;

const TitleGroup = styled.div`
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 12px;
    text-align: center;
    width: 100%;
`;

const Heading = styled.h1`
    color: #000000;
    font-family: ${postGroteskFont};
    font-size: 37px;
    font-weight: 500;
    letter-spacing: -0.37px;
    line-height: 1.28;
    margin: 0;
    max-width: 100%;
    
    ${breakPoints.sm`
        font-size: 30px;
        line-height: 1.3;
    `}
`;

const Reminder = styled.p.attrs({ "data-test-id": "signup-pricing-reminder" })`
    color: #000000;
    font-family: ${postGroteskFont};
    font-size: 16px;
    font-weight: 400;
    line-height: 1.4;
    margin: 0;
    
    ${breakPoints.sm`
        font-size: 14px;
    `}
`;

const PricingGrid = styled.div.attrs({ "data-test-id": "signup-pricing-grid" })`
    display: grid;
    flex: 1 1 auto;
    gap: 28px;
    grid-template-columns: repeat(3, 320px);
    min-height: 0;
    width: 100%;
    justify-content: center;
    
    ${breakPoints.lg`
        grid-template-columns: repeat(2, minmax(270px, 320px));
    `}

    ${stackBreakpoint`
        grid-template-columns: minmax(0, 1fr);
        justify-items: center;
    `}
`;

const PlanCard = styled.article.attrs({ "data-test-id": "signup-pricing-plan-card" }) <{ $highlighted: boolean }>`
    align-items: stretch;
    background: #ffffff;
    border: 1px solid ${({ $highlighted }) => $highlighted ? "rgba(0, 148, 204, 0.6)" : "rgba(0, 9, 13, 0.2)"};
    border-radius: 12px;
    box-shadow: ${({ $highlighted }) => $highlighted ? "6px 12px 45px 0 rgba(88, 204, 248, 0.50);" : "none"};
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    padding: 22px 28px;
    position: relative;
    max-width: 320px;
    width: 100%;
    
    ${breakPoints.xs`
        padding: 22px;
    `}
`;

const PlanHeader = styled.div`
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 18px;
    width: 100%;

    ${breakPoints.xs`
        gap: 16px;
    `}
`;

const PlanName = styled.h2.attrs({ "data-test-id": "signup-pricing-plan-name" })`
    color: #0094cc;
    font-family: ${postGroteskFont};
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    margin: 0;
    width: 100%;
`;

const PriceGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`;

const ProPriceGroup = styled(PriceGroup)`
    ${breakPoints.xs`
        align-items: baseline;
        flex-direction: row;
        gap: 24px;
    `}
`;

const PriceLine = styled.div.attrs({ "data-test-id": "signup-pricing-price-line" })`
    align-items: baseline;
    color: #000000;
    display: flex;
    font-family: ${postGroteskFont};
    gap: 1px;
    white-space: nowrap;
`;

const Price = styled.span`
    font-size: 40px;
    font-weight: 500;
    letter-spacing: -0.8px;
    line-height: 1.2;
`;

const PriceUnit = styled.span`
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.96px;
    line-height: 1.3;
    text-transform: uppercase;
`;

const BillingNote = styled.p.attrs({ "data-test-id": "signup-pricing-billing-note" })`
    color: #000000;
    font-family: ${postGroteskFont};
    font-size: 12px;
    font-weight: 400;
    line-height: 1.45;
    margin: 0;
    margin-bottom: 10px;

    ${stackBreakpoint`
        margin-bottom: 0;
    `}
    
    ${breakPoints.xs`
        margin-bottom: 0;
    `}
`;

const BillingToggle = styled.div`
    align-items: center;
    background: rgba(216, 218, 218, 0.3);
    border: 0.5px solid #dcddde;
    border-radius: 4px;
    display: flex;
    height: 28px;
    overflow: hidden;
    width: 100%;
`;

const BillingToggleItem = styled.span.attrs<{ $active?: boolean }>(
    ({ $active, className }) => ({
        className: [className, $active && "active"].filter(Boolean).join(" "),
        "data-test-id": "signup-pricing-billing-toggle-item",
    })
) <{ $active?: boolean }>`
    align-items: center;
    background: ${({ $active }) => $active ? "#192125" : "#D8DADA73"};
    color: ${({ $active }) => $active ? "#FFFFFF" : "#B2B5B6"};
    display: flex;
    flex: 1 1 0;
    font-family: ${postGroteskFont};
    font-size: 12px;
    font-weight: 400;
    height: 100%;
    justify-content: center;
    line-height: 1.45;
    min-width: 0;
    padding: 4px 8px;
    white-space: nowrap;
    cursor: pointer;
`;

const Divider = styled.div`
    background: #afb1b2;
    flex: 0 0 auto;
    height: 1px;
    width: 100%;
    order: 2;

    ${breakPoints.xs`
        order: 4;
    `}
`;

const FeatureList = styled.ul.attrs({ "data-test-id": "signup-pricing-feature-list" })`
    order: 3;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 9px;
    list-style: none;
    margin: 0;
    min-height: 0;
    padding: 0;
    padding-bottom: 4px;

    ${breakPoints.xs`
        order: 4;
    `}
`;

const FeatureItem = styled.li`
    align-items: flex-start;
    color: #001a24;
    display: flex;
    font-family: ${postGroteskFont};
    font-size: 14px;
    font-weight: 400;
    gap: 6px;
    line-height: 1.32;
    min-width: 0;
`;

const CheckIcon = styled.span`
    flex: 0 0 14px;
    height: 18px;
    position: relative;
    width: 14px;

    &::after {
        border-bottom: 1.5px solid #0094cc;
        border-right: 1.5px solid #0094cc;
        content: "";
        height: 7px;
        left: 4px;
        position: absolute;
        top: 4px;
        transform: rotate(45deg);
        width: 4px;
    }
`;

const FeatureText = styled.span`
    min-width: 0;
`;

const ButtonFrame = styled.div`
    order: 4;
    align-items: flex-end;
    display: flex;
    flex: 0 0 auto;
    height: 40px;
    justify-content: flex-start;
    width: 100%;

    ${breakPoints.xs`
        order: 3;
    `}
`;

const TrialButton = styled(PrimaryButton)`
    height: 40px;
    min-width: 178px;
    padding: 10px 24px;
    
    ${breakPoints.xs`
        width: 100%;
    `}
    
`;

const TrustPanel = styled.aside`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 26px;
    justify-content: center;
    min-height: 0;
    padding: 14px 0;
    max-width: 320px;
    
    ${breakPoints.lg`
        display: none;
    `}

    ${breakPoints.xs`
        display: flex;
        color: #001A24;
    `}
`;

const G2Block = styled.div.attrs({ "data-test-id": "signup-pricing-g2-block" })`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 9px;
`;

const G2Image = styled.span.attrs({ "data-test-id": "signup-pricing-g2-image" })`
    display: block;
    height: 25px;
    width: 145px;
    mask: url(${getStaticUrl(`${pricingAssetPath}g2-stars.svg`)}) no-repeat center / contain;
    background: #7F8486;

    ${breakPoints.xs`
        background: #FF492C
    `}
`;

const G2Caption = styled.p`
    color: rgba(0, 9, 13, 0.4);
    font-family: ${postGroteskFont};
    font-size: 13px;
    font-weight: 400;
    line-height: 1.45;
    margin: 0;

    ${breakPoints.xs`
        color: #38393A
    `}
`;

const QuoteCard = styled.figure.attrs({ "data-test-id": "signup-pricing-quote-card" })`
    border: 0.4px solid rgba(0, 9, 13, 0.2);
    border-radius: 8px;
    margin: 0;
    padding: 22px;
    width: 100%;
`;

const QuoteText = styled.blockquote`
    color: rgba(0, 9, 13, 0.4);
    font-family: ${postGroteskFont};
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: 1.4;
    margin: 0 0 18px;

    ${breakPoints.xs`
        color: #38393A
    `}
`;

const QuoteAttribution = styled.figcaption`
    color: rgba(0, 9, 13, 0.6);
    display: flex;
    flex-direction: column;
    font-family: ${postGroteskFont};
    font-size: 11px;
    gap: 4px;
    line-height: 1.5;
`;

const QuoteName = styled.span`
    font-weight: 500;

    ${breakPoints.xs`
        color: #38393A
    `}
`;

const QuotePosition = styled.span`
    ${breakPoints.xs`
        color: #38393A
    `}
`;

const CompanyBlock = styled.div.attrs({ "data-test-id": "signup-pricing-company-block" })`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const CompanyHeading = styled.p`
    color: #7f8486;
    font-family: ${postGroteskFont};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    margin: 0;
    text-align: center;

    ${breakPoints.xs`
        color: #38393A
    `}
`;

const LogoRow = styled.div.attrs({ "data-test-id": "signup-pricing-logo-row" })`
    align-items: center;
    display: flex;
    justify-content: center;
    width: 100%;

    ${breakPoints.xs`
        flex-wrap: wrap;
        gap: 12px;
        max-width: 284px;
        padding: 4px 0;
    `}
`;

const LogoFrame = styled.span`
    align-items: center;
    display: flex;
    height: 21px;
    justify-content: center;
    overflow: hidden;
    width: 51px;

    ${breakPoints.xs`
        height: 32px;
        width: 82px;
    `}
`;

const CompanyLogo = styled.img`
    display: block;
    height: auto;
    width: auto;
`;

const BottomCopy = styled.p.attrs({ "data-test-id": "signup-pricing-bottom-copy" })`
    color: #000000;
    flex: 0 0 auto;
    font-family: ${postGroteskFont};
    font-size: 16px;
    font-weight: 400;
    line-height: 1.4;
    margin: 0;
    text-align: center;
    width: 100%;
    
    ${breakPoints.sm`
        font-size: 14px;
    `}
`;

const ProMonthlyButton = styled.button`
    background: transparent;
    border: 0;
    color: #0094cc;
    font: inherit;
    font-weight: 500;
    margin: 0;
    padding: 0;
    text-decoration: underline;
    cursor: pointer;
`;

type PricingPlan = {
    name: string;
    price: Record<string, number>;
    unit: string;
    highlighted: boolean;
    features: string[];
    billingNote?: string;
}

const proPlanBase = {
    name: "Pro Plan — For one user",
    unit: "/mo",
    billingNote: "Billed annually",
    highlighted: true,
    features: [
        "Unlimited AI content generation",
        "Create with AI, step by step workflow",
        "Apply your custom brand styling",
        "Over 300 Smart Slide layouts",
        "Hundreds of designer template decks",
        "Stock image & icon libraries",
        "Dynamic data visualization",
        "Embed videos & narration"
    ]
};

const teamPlanBase = {
    name: "Team Plan — Invite teammates next",
    unit: "/mo/user",
    highlighted: false,
    features: [
        "Everything in Pro...",
        "Live collaborative editing",
        "Centralized slide & template library",
        "Version control & locked slides",
        "Viewer analytics (for external sharing)",
        "Live data linking to charts & tables",
        "Shared brand library (image & assets)"
    ]
};

function ProPlan({ plan, startCheckout }: { plan: PricingPlan, startCheckout: (plan: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM, interval: "year" | "month") => void }) {
    return (
        <PlanCard $highlighted={plan.highlighted}>
            <PlanHeader>
                <PlanName>{t(plan.name)}</PlanName>
                <ProPriceGroup>
                    <PriceLine>
                        <Price>{`$${formatPlanCardPrice(plan.price.year)}`}</Price>
                        <PriceUnit>{t(plan.unit)}</PriceUnit>
                    </PriceLine>
                    <BillingNote>{t(plan.billingNote)}</BillingNote>
                </ProPriceGroup>
            </PlanHeader>
            <Divider />
            <FeatureList>
                {plan.features.map(feature => (
                    <FeatureItem key={feature}>
                        <CheckIcon aria-hidden="true" />
                        <FeatureText>{t(feature)}</FeatureText>
                    </FeatureItem>
                ))}
            </FeatureList>
            <ButtonFrame>
                <TrialButton type="button" onClick={() => {
                    startCheckout(WorkspacePlanTier.PRO, "year");
                }}>{t("Start free trial")}</TrialButton>
            </ButtonFrame>
        </PlanCard>
    );
}

function TeamPlan({ plan, startCheckout }: { plan: PricingPlan, startCheckout: (plan: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM, interval: "year" | "month") => void }) {
    const [interval, setInterval] = useState<"year" | "month">("year");

    return (
        <PlanCard $highlighted={plan.highlighted}>
            <PlanHeader>
                <PlanName>{t(plan.name)}</PlanName>
                <PriceGroup>
                    <PriceLine>
                        <Price>{`$${formatPlanCardPrice(plan.price[interval])}`}</Price>
                        <PriceUnit>{t(plan.unit)}</PriceUnit>
                    </PriceLine>
                    <BillingToggle aria-label={t("Billing period")}>
                        <BillingToggleItem $active={interval === "month"} onClick={() => setInterval("month")}>
                            {`Billed monthly`}
                        </BillingToggleItem>
                        <BillingToggleItem $active={interval === "year"} onClick={() => setInterval("year")}>
                            {"Billed annually"}
                        </BillingToggleItem>
                    </BillingToggle>
                </PriceGroup>
            </PlanHeader>
            <Divider />
            <FeatureList>
                {plan.features.map(feature => (
                    <FeatureItem key={feature}>
                        <CheckIcon aria-hidden="true" />
                        <FeatureText>{t(feature)}</FeatureText>
                    </FeatureItem>
                ))}
            </FeatureList>
            <ButtonFrame>
                <TrialButton type="button"
                    onClick={() => {
                        startCheckout(WorkspacePlanTier.TEAM, interval);
                    }}>
                    {t("Start free trial")}
                </TrialButton>
            </ButtonFrame>
        </PlanCard>
    );
}

export function PricingDialog({
    header = "First, choose your trial plan. Free for 14 days!",
    startCheckout,
    onMounted,
    fullWidth = true
}: {
    header?: string;
    startCheckout: (plan: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM, interval: "year" | "month") => void;
    onMounted: () => void;
    fullWidth?: boolean;
}) {
    useEffect(() => {
        onMounted();
    }, []);

    const proPlan = useMemo<PricingPlan>(() => ({
        ...proPlanBase,
        price: {
            year: StripeUtils.Plans.getMonthlyEquivalentPrice(
                StripeUtils.Plans.getDefaultRecurringPlanId(WorkspacePlanTier.PRO, "year")
            ),
        },
    }), []);

    const teamPlan = useMemo<PricingPlan>(() => ({
        ...teamPlanBase,
        price: {
            year: StripeUtils.Plans.getMonthlyEquivalentPrice(StripeUtils.Plans.getDefaultRecurringPlanId(WorkspacePlanTier.TEAM, "year")),
            month: StripeUtils.Plans.getMonthlyEquivalentPrice(StripeUtils.Plans.getDefaultRecurringPlanId(WorkspacePlanTier.TEAM, "month")),
        },
    }), []);

    return (
        <TourCard.CardFrame $fullWidth={fullWidth}>
            <TourCard.Card aria-labelledby="onboarding-pricing-heading">
                <PricingContent>
                    <TitleGroup>
                        <Heading id="onboarding-pricing-heading">
                            {t(header)}
                        </Heading>
                        <Reminder>
                            {t("We’ll send you an email reminder 48 hours before your trial expires.")}
                        </Reminder>
                    </TitleGroup>
                    <PricingGrid>
                        <ProPlan key={"pro"} plan={proPlan} startCheckout={startCheckout} />
                        <TeamPlan key={"team"} plan={teamPlan} startCheckout={startCheckout} />
                        <TrustPanel>
                            <G2Block>
                                <G2Image role="img" aria-label={t("G2 rating")} />
                                <G2Caption>{t("4.7 - G2 Reviews")}</G2Caption>
                            </G2Block>
                            <QuoteCard>
                                <QuoteText>
                                    {t("“Beautiful.ai is a game changer for creating beautiful, engaging content at unheard of speed. Their Smart Slides and AI tools have helped us to create amazing stuff.”")}
                                </QuoteText>
                                <QuoteAttribution>
                                    <QuoteName>{t("Ryan Heinl")}</QuoteName>
                                    <QuotePosition>{t("Executive Director, Wake Forest University")}</QuotePosition>
                                </QuoteAttribution>
                            </QuoteCard>
                            <CompanyBlock>
                                <CompanyHeading>
                                    {t("Trusted by over 100,000 companies worldwide")}
                                </CompanyHeading>
                                <LogoRow>
                                    {trustLogos.map(logo => (
                                        <LogoFrame key={logo.alt}>
                                            <CompanyLogo
                                                alt={logo.alt}
                                                src={logo.src}
                                            />
                                        </LogoFrame>
                                    ))}
                                </LogoRow>
                            </CompanyBlock>
                        </TrustPanel>
                    </PricingGrid>
                    <BottomCopy>
                        {t("Need just one deck? $45 one-time — includes full Pro plan features, cancel anytime.")}{" "}
                        <ProMonthlyButton type="button" onClick={() => {
                            startCheckout(WorkspacePlanTier.PRO, "month");
                        }}>
                            {t("Get started.")}
                        </ProMonthlyButton>
                    </BottomCopy>
                </PricingContent>
            </TourCard.Card>
        </TourCard.CardFrame>
    );
}

export const Pricing = AuthController.withState(function Pricing() {
    return (
        <PricingDialog
            startCheckout={(plan, interval) => AuthController.startCheckout(plan, interval)}
            onMounted={() => AuthController.setPageLoaded()}
        />
    );
});
